// Open-Meteo — free, keyless, no rate-limit auth needed for our volume.
// Geocoding: turns a city/country name into {lat, lon} for both Climate's
// own lookup and (once geocoded) a more precise Wunderground lat/lon link
// for the Weather menu item, instead of its free-text search.
const GEOCODE_URL = "https://geocoding-api.open-meteo.com/v1/search";
// 10 full calendar years of daily highs/lows/precip — averaged client-side
// into a "typical month" summary. Not Open-Meteo's newer Climate API (that
// one is future climate-*change* projections, a different product) — this
// is the plain Historical Weather archive, which is what "typical climate
// for this month" actually means.
const ARCHIVE_URL = "https://archive-api.open-meteo.com/v1/archive";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

export async function geocode(city, country) {
  const name = [city, country].filter(Boolean).join(", ");
  if (!name) return null;
  const url = `${GEOCODE_URL}?name=${encodeURIComponent(city || country)}&count=1&language=en&format=json`;
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`Geocoding failed: HTTP ${resp.status}`);
  const data = await resp.json();
  const hit = data.results?.[0];
  if (!hit) return null;
  return {
    lat: hit.latitude,
    lon: hit.longitude,
    country: hit.country,
    name: hit.name,
    resolvedName: [hit.name, hit.country].filter(Boolean).join(", "),
  };
}

// For a city guessed from a URL slug, where the trailing words may be a
// country/region we don't recognize (e.g. "Prague Czechia"): tries the whole
// name, then drops trailing words until Open-Meteo finds a match.
export async function geocodeGuess(city, country) {
  const words = String(city || "").split(/\s+/).filter(Boolean);
  for (let n = words.length; n >= 1; n--) {
    const g = await geocode(words.slice(0, n).join(" "), country);
    if (g) return g;
  }
  return null;
}

// Countries that primarily use Fahrenheit day-to-day — used only to pick a
// sensible default unit for a newly-opened Climate dialog; the toggle
// always lets the user override it.
export const FAHRENHEIT_COUNTRIES = new Set(["United States", "Bahamas", "Cayman Islands", "Liberia"]);

// Returns [{month, monthName, avgHigh, avgLow, avgPrecip, avgSnow,
// avgHumidity, avgSunshineHours}], one entry per calendar month, averaged
// over the last `years` full years of daily data. unit: "celsius" (default)
// or "fahrenheit" — Open-Meteo converts server side via temperature_unit,
// so no client-side C-to-F math to get wrong. Precipitation/snow switch to
// inches alongside it (the imperial convention that normally pairs with
// Fahrenheit), via precipitation_unit.
export async function getMonthlyClimateAverages(lat, lon, years = 10, unit = "celsius") {
  const endYear = new Date().getFullYear() - 1; // last full year
  const startYear = endYear - years + 1;
  const imperial = unit === "fahrenheit";
  const url = `${ARCHIVE_URL}?latitude=${lat}&longitude=${lon}` +
    `&start_date=${startYear}-01-01&end_date=${endYear}-12-31` +
    `&daily=temperature_2m_max,temperature_2m_min,precipitation_sum,snowfall_sum,` +
    `relative_humidity_2m_mean,sunshine_duration&timezone=auto` +
    `&temperature_unit=${imperial ? "fahrenheit" : "celsius"}` +
    `&precipitation_unit=${imperial ? "inch" : "mm"}`;
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`Climate data fetch failed: HTTP ${resp.status}`);
  const data = await resp.json();
  const {
    time = [],
    temperature_2m_max = [],
    temperature_2m_min = [],
    precipitation_sum = [],
    snowfall_sum = [],
    relative_humidity_2m_mean = [],
    sunshine_duration = [],
  } = data.daily || {};

  const buckets = Array.from({ length: 12 }, () => ({
    highSum: 0, lowSum: 0, precipSum: 0, snowSum: 0, humiditySum: 0, sunshineSum: 0,
    count: 0, humidityCount: 0,
  }));
  for (let i = 0; i < time.length; i++) {
    const month = parseInt(time[i].split("-")[1], 10) - 1;
    if (temperature_2m_max[i] == null || temperature_2m_min[i] == null) continue;
    const b = buckets[month];
    b.highSum += temperature_2m_max[i];
    b.lowSum += temperature_2m_min[i];
    b.precipSum += precipitation_sum[i] || 0;
    b.snowSum += snowfall_sum[i] || 0;
    b.sunshineSum += sunshine_duration[i] || 0;
    b.count += 1;
    // Humidity has its own gaps in some locations/years — average only
    // over days it's actually present, not the same denominator as temp.
    if (relative_humidity_2m_mean[i] != null) {
      b.humiditySum += relative_humidity_2m_mean[i];
      b.humidityCount += 1;
    }
  }

  return buckets.map((b, i) => ({
    month: i + 1,
    monthName: MONTH_NAMES[i],
    avgHigh: b.count > 0 ? b.highSum / b.count : null,
    avgLow: b.count > 0 ? b.lowSum / b.count : null,
    // "Typical total for the month", not the average of a single day —
    // average daily amount × a typical month's day count.
    avgPrecip: b.count > 0 ? (b.precipSum / b.count) * DAYS_IN_MONTH[i] : null,
    avgSnow: b.count > 0 ? (b.snowSum / b.count) * DAYS_IN_MONTH[i] : null,
    avgHumidity: b.humidityCount > 0 ? b.humiditySum / b.humidityCount : null,
    // seconds/day -> hours/day
    avgSunshineHours: b.count > 0 ? (b.sunshineSum / b.count) / 3600 : null,
    tempUnit: imperial ? "°F" : "°C",
    precipUnit: imperial ? "in" : "mm",
  }));
}
